// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Button, CircularProgress } from '@mui/material';
import { type TargetAssetState } from '@shared/data-access/interfaces/api/ai/target-strategy';
import { numberToFixed } from '@shared/ui-kit/utils';
import {
  PortfolioPositions,
  useValidateTickersPortfolio,
} from '@advisor-app/features/portfolio';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import { AppIcon, PageContainer } from '@shared/ui-kit/components';
import { type PortfolioLoaded } from '@shared/data-access/interfaces/common/portfolio-loaded';
import { type PortfolioPositionsCurrent } from '@shared/data-access/interfaces/redux/portfolio-state';
import { useAppNavigate, useValidationPortfolioLoaded } from '@advisor-app/hooks';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectBenchmark,
  selectHistoricalWindow,
  setHistoricalWindow,
  setTaxObjective,
} from '@advisor-app/store/slices/parameters';
import {
  portfolioPositionsSlice,
  selectCurrentBalance,
  selectCurrentPortfolio,
  selectPortfolioLoaded,
} from '@shared/data-access/slices/portfolio-positions';
import {
  selectWorkflowAsset,
  selectWorkflowType,
} from '@advisor-app/store/slices/workflow';
import { useTranslation } from 'react-i18next';
import { useNavigation } from 'react-router';

type PositionsTable = PortfolioLoaded[] | [];

export const Portfolio: FC<{ to: Paths }> = ({ to }) => {
  const navigate = useAppNavigate();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'components', 'glossary']);

  const portfolio = useAppSelector(selectCurrentPortfolio);

  const initialTableData: PositionsTable = useMemo(() => {
    return portfolio.map(asset => ({
      outstanding_balance: asset.qty * asset.price,
      ticker: asset.ticker,
      unrealized_capital_gains: asset.unrealized_capital_gains,
      date: asset?.date,
      weight: 0,
    }));
  }, [portfolio]);

  const [positionsTableData, setPositionTableData] =
    useState<PositionsTable>(initialTableData);
  const [invalidDeleted, setInvalidDeleted] = useState(false);

  const portfolioNameUploaded = useAppSelector(selectPortfolioLoaded);
  const [benchmark] = useAppSelector(state => selectBenchmark(state, 'ai-builder'));
  const typeAsset = useAppSelector(selectWorkflowAsset);
  const workflowType = useAppSelector(selectWorkflowType);
  const currentBalance = useAppSelector(selectCurrentBalance);
  const historicalWindow = useAppSelector(selectHistoricalWindow);

  const convertedPositions: PortfolioLoaded[] = portfolio.map(asset => ({
    outstanding_balance: asset.outstanding_balance,
    ticker: asset.ticker,
    unrealized_capital_gains: asset.unrealized_capital_gains,
    date: asset.date,
    weight: 0,
  }));

  const { isPortfolioLoaded } = useValidationPortfolioLoaded();

  const { isSomeInvalidTicker, invalidTickers, hasValues, isLoadingValidation } =
    useValidateTickersPortfolio({
      benchmark: benchmark.ticker,
    });

  const isAnalyzeDisabled = useMemo(
    () =>
      !isPortfolioLoaded ||
      isSomeInvalidTicker ||
      !hasValues ||
      isLoadingValidation ||
      !positionsTableData.some(
        ({ ticker, outstanding_balance }) =>
          ticker !== '$CASH' && (outstanding_balance ?? 0) > 0.01,
      ),
    [
      isPortfolioLoaded,
      isSomeInvalidTicker,
      hasValues,
      isLoadingValidation,
      positionsTableData,
    ],
  );

  const isNavigating = navigation.state === 'loading';

  const updateMyData = (rowIndex: number, columnId: string, value: any) => {
    let newValue: string | number;
    if (
      columnId === 'outstanding_balance' ||
      columnId === 'unrealized_capital_gains'
    ) {
      newValue = Number(value);
      if (isNaN(value)) return;
    } else {
      newValue = value;
    }

    setPositionTableData(old =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return { ...old[rowIndex], [columnId]: newValue };
        }
        return row;
      }),
    );
  };

  const handleDeleteInvalidPositions = () => {
    const filteredInvalidTickers = positionsTableData.filter(
      position => !invalidTickers.includes(position.ticker),
    );
    setPositionTableData(filteredInvalidTickers);
    setInvalidDeleted(true);
  };

  const handleDeleteAllPositions = () => {
    showModal({
      ...MODAL_TEMPLATES_OPTIONS(t).CONFIRM_DELETE,
      icon: <AppIcon icon="ExclamationMark" fill="#1072BA" />,
      title: t('portfolio.delete-positions', { ns: 'components' }),
      content: t('portfolio.delete-positions-confirmation', { ns: 'components' }),
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        setPositionTableData([]);
        dispatch(portfolioPositionsSlice.actions.reset());
      }
    });
  };

  const pageHeaderText = useMemo(
    () =>
      ({
        rebalance: t('common.rebalance', { ns: 'common' }),
        'tax-harvesting': t('common.tax-manager', { ns: 'common' }),
      }?.[workflowType as string] ??
      t('glossary.portfolio-analysis', { ns: 'glossary' })),
    [workflowType, t],
  );

  const tableHeaderText = useMemo(
    () =>
      portfolioNameUploaded ||
      t('portfolio.insert-portfolio', { ns: 'components' }),
    [portfolioNameUploaded, t],
  );

  useEffect(() => {
    if (portfolio.length > 0) {
      setPositionTableData(convertedPositions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only sync from loaded portfolio
  }, []);

  const filteredDataTable = positionsTableData.filter(
    ({ ticker }) => ticker !== '$CASH',
  );

  useEffect(() => {
    const newHoldings: PortfolioPositionsCurrent[] = filteredDataTable.map(
      position => {
        const holding = portfolio.find(
          h => h.ticker === position.ticker,
        ) as PortfolioPositionsCurrent;
        const price = holding?.price ?? 1;
        return {
          ticker: position.ticker,
          date: position.date as string,
          unrealized_capital_gains: position.unrealized_capital_gains,
          qty: numberToFixed((position.outstanding_balance ?? 0) / price, 2),
          price,
          harvesting: { percentage: 0, status: 0, replacement: null },
        };
      },
    );

    dispatch(
      portfolioPositionsSlice.actions.setCurrentPortfolioPositions(newHoldings),
    );
    dispatch(setTaxObjective('Manually'));

    if (!positionsTableData.length && invalidDeleted) {
      dispatch(portfolioPositionsSlice.actions.reset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- table edits only; extra deps re-dispatch in a loop
  }, [positionsTableData]);

  useEffect(() => {
    const targetAsset: TargetAssetState[] = portfolio
      .map(asset => ({
        ticker: asset.ticker,
        weight:
          Math.round((asset.outstanding_balance / currentBalance) * 100 * 1000) /
          1000,
        details: asset.details,
      }))
      .sort((a, b) => b.weight - a.weight);

    dispatch(
      portfolioPositionsSlice.actions.setTargetPortfolioPositions(targetAsset),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow portfolio rows only
  }, [portfolio]);

  useEffect(() => {
    if (historicalWindow !== 365) dispatch(setHistoricalWindow(365));
    dispatch(portfolioPositionsSlice.actions.resetTradesTable());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only window/trades reset
  }, []);

  return (
    <PageContainer
      titlePage={pageHeaderText}
      actionBtn={
        <Button
          color="secondary"
          disabled={isAnalyzeDisabled || isNavigating}
          onClick={() => navigate(to)}
          endIcon={
            isNavigating ? (
              <CircularProgress size={16} color="secondary" />
            ) : undefined
          }
        >
          {isLoadingValidation
            ? t('common.validating', { ns: 'common' })
            : t('common.analyze', { ns: 'common' })}
        </Button>
      }
    >
      <PortfolioPositions
        filteredDataTable={filteredDataTable}
        handleDeleteAllPositions={handleDeleteAllPositions}
        handleDeleteInvalidPositions={handleDeleteInvalidPositions}
        invalidTickers={invalidTickers}
        isSomeInvalidTicker={isSomeInvalidTicker}
        positionsTableData={positionsTableData}
        setPositionTableData={setPositionTableData}
        portfolio={portfolio}
        typeAsset={typeAsset}
        updateMyData={updateMyData}
        workflowType={workflowType}
        tableHeaderText={tableHeaderText}
        portfolioBenchmark={benchmark.ticker}
        mode="outstanding_balance"
        totalWeight={100}
      />
    </PageContainer>
  );
};

export default Portfolio;
